import { UploadIcon } from 'lucide-preact';
import { HTMLAttributes } from 'preact/compat';
import { useRouter } from 'preact-router';

import { cn } from '@/lib/utils';
import { useSelector } from '@/redux/helper';
import { selectCuratesHighlights } from '@/redux/selectors/users';
import UploadMediaButton from '@/routes/habitat/components/Album/UploadMediaButton';
import { trackMP } from '@/shared/mp';

import FormulaFilterPanel from './FormulaFilterPanel';
import { FormulaHighlightType, HighlightType } from './types';

interface HighlightsListHeaderProps extends HTMLAttributes<HTMLDivElement> {
  type: HighlightType;
  onTypeChange: (type: HighlightType) => void;
  selectedFormulas?: FormulaHighlightType[];
  onFormulaChange?: (formulas: FormulaHighlightType[]) => void;
}

const TYPES = [
  {
    label: 'Highlights',
    value: 'highlight',
    hidden: false,
  },
  {
    label: 'Latest Clips',
    value: 'clip',
    hidden: false,
  },
  {
    label: 'Behaviors',
    value: 'behavior',
    hidden: false,
  },
  {
    label: 'All Highlights (Admin)',
    value: 'curated',
    hidden: true,
  },
] as const;

const HighlightsListHeader = ({
  onTypeChange,
  type: highlightType,
  selectedFormulas,
  onFormulaChange,
  className,
  ...rest
}: HighlightsListHeaderProps) => {
  const [{ path }] = useRouter();
  const isAdmin = useSelector((state) => state.user.role === 'admin');
  const curatesHighlights = useSelector(selectCuratesHighlights);
  const types = curatesHighlights ? TYPES : TYPES.filter((type) => !type.hidden);

  return (
    <div
      className={cn('preflight flex justify-between gap-2 bg-white py-3 mobile:flex-wrap', className)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      <div className="flex min-w-0 grow items-center gap-2">
        <div
          role="radiogroup"
          aria-label="Highlights filter"
          className="preflight preflight-fieldset flex items-center gap-2"
        >
          {types.map(({ label, value, hidden }) => (
            <a
              data-native
              key={value}
              role="radio"
              href={`?type=${value}`}
              aria-checked={highlightType === value}
              data-hidden={hidden}
              className="preflight preflight-a rounded-lg bg-primary-green bg-opacity-15 px-2 py-1 text-xs/none text-primary-green hover:bg-opacity-100 hover:text-white aria-checked:bg-opacity-100 aria-checked:text-white data-[hidden=false]:shrink-0 data-[hidden=true]:opacity-50"
              onClick={(e) => {
                e.preventDefault();
                onTypeChange(value);
                trackMP('highlight-filter-changed', {
                  entryPoint: path === '/home' ? 'home-page' : 'highlights-page',
                });
              }}
            >
              {label}
            </a>
          ))}
        </div>
        {isAdmin && highlightType === 'clip' && selectedFormulas && onFormulaChange && (
          <div className="mobile:hidden">
            <FormulaFilterPanel selectedFormulas={selectedFormulas} onFormulaChange={onFormulaChange} />
          </div>
        )}
      </div>
      {curatesHighlights && highlightType === 'curated' && (
        <div className="">
          <UploadMediaButton
            disallowSendToChat
            uploadToTab="curated"
            className="!min-w-0"
            label={
              <>
                <UploadIcon className="size-[1em]" />
                <span className="sr-only">Upload Curated</span>
              </>
            }
          />
        </div>
      )}
    </div>
  );
};

export default HighlightsListHeader;
